import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, metaAPI } from '../../lib/api';
import { Link } from 'react-router-dom';
import { Zap, Clock, XCircle, Plus, ChevronLeft, ChevronRight, Loader2, AlertTriangle, X, Download, Search, FileText, Tag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

function timeRemaining(endDateStr) {
  const end = new Date(endDateStr);
  const now = new Date();
  const diff = end - now;
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  return `${hours}h left`;
}

function formatDate(str) {
  if (!str) return '-';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PAYMENT_METHODS = [
  { value: 'comp', label: 'Comp / Free' },
  { value: 'offline', label: 'Offline' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_BADGE = {
  comp: { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Comp' },
  offline: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Offline' },
  manual: { bg: 'bg-sky-50', text: 'text-sky-700', label: 'Manual' },
  other: { bg: 'bg-zinc-100', text: 'text-zinc-600', label: 'Other' },
  stripe: { bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'Stripe' },
};

function PaymentBadge({ method }) {
  const badge = PAYMENT_BADGE[method] || PAYMENT_BADGE.stripe;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.bg} ${badge.text}`}>
      {badge.label}
    </span>
  );
}

function ExpireDialog({ promo, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="expire-dialog-overlay">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6" data-testid="expire-dialog">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-zinc-900">Expire Promotion</h3>
            <p className="text-sm text-zinc-500">This will immediately end the promotion for <strong>{promo.listing?.title || 'this listing'}</strong>.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50" data-testid="expire-dialog-cancel">Cancel</button>
          <button onClick={handleConfirm} disabled={loading} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="expire-dialog-confirm">
            {loading && <Loader2 className="w-3 h-3 animate-spin" />} Expire Now
          </button>
        </div>
      </div>
    </div>
  );
}

function ExtendDialog({ promo, onClose, onConfirm }) {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    if (days < 1) { toast.error('Enter at least 1 day'); return; }
    setLoading(true);
    await onConfirm(days);
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="extend-dialog-overlay">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6" data-testid="extend-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-zinc-900">Extend Promotion</h3>
          <button onClick={onClose} className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600" data-testid="extend-dialog-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Extend promotion for <strong>{promo.listing?.title || 'this listing'}</strong>.</p>
        <div className="mb-4">
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Days to add</label>
          <input type="number" min="1" value={days} onChange={e => setDays(parseInt(e.target.value) || 0)}
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
            data-testid="extend-days-input"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50" data-testid="extend-dialog-cancel">Cancel</button>
          <button onClick={handleConfirm} disabled={loading} className="px-4 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002585] disabled:opacity-50 flex items-center gap-2" data-testid="extend-dialog-confirm">
            {loading && <Loader2 className="w-3 h-3 animate-spin" />} Extend
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatePromotionModal({ open, onOpenChange, onCreated }) {
  const [tiers, setTiers] = useState([]);
  const [listingSearch, setListingSearch] = useState('');
  const [listingResults, setListingResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [durationOverride, setDurationOverride] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('comp');
  const [internalNote, setInternalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      metaAPI.getPromotionTiers().then(r => setTiers(r.data)).catch(() => {});
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setListingSearch('');
    setListingResults([]);
    setSelectedListing(null);
    setSelectedTier('');
    setStartDate(new Date().toISOString().slice(0, 10));
    setDurationOverride('');
    setAmountPaid('');
    setPaymentMethod('comp');
    setInternalNote('');
  };

  // When tier changes, update amount
  useEffect(() => {
    if (selectedTier) {
      const tier = tiers.find(t => t.id === selectedTier);
      if (tier) setAmountPaid(String(tier.price_usd));
    }
  }, [selectedTier, tiers]);

  const searchListings = useCallback(async (q) => {
    if (!q || q.length < 2) { setListingResults([]); return; }
    setSearchLoading(true);
    try {
      const { data } = await adminAPI.getListings({ search: q, status: 'active', limit: 8 });
      setListingResults(data.listings || []);
    } catch { setListingResults([]); }
    finally { setSearchLoading(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchListings(listingSearch), 300);
    return () => clearTimeout(timer);
  }, [listingSearch, searchListings]);

  const handleSubmit = async () => {
    if (!selectedListing) { toast.error('Select a listing'); return; }
    if (!selectedTier) { toast.error('Select a promotion tier'); return; }
    setSubmitting(true);
    try {
      const payload = {
        listing_id: selectedListing.listing_id,
        tier_id: selectedTier,
        start_date: new Date(startDate + 'T00:00:00Z').toISOString(),
        payment_method: paymentMethod,
        internal_note: internalNote,
      };
      if (durationOverride && parseInt(durationOverride) > 0) payload.duration_override = parseInt(durationOverride);
      if (amountPaid !== '') payload.amount_paid = parseFloat(amountPaid);
      await adminAPI.createPromotion(payload);
      toast.success('Promotion created successfully');
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create promotion');
    } finally { setSubmitting(false); }
  };

  const selectedTierObj = tiers.find(t => t.id === selectedTier);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-[#002FA7]" /> Create Promotion</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          {/* Listing Search */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Listing *</label>
            {selectedListing ? (
              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                <div className="w-10 h-10 rounded bg-zinc-200 overflow-hidden flex-shrink-0">
                  {selectedListing.images?.[0] && <img src={selectedListing.images[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900 truncate">{selectedListing.title}</p>
                  <p className="text-xs text-zinc-500">${selectedListing.price?.toFixed(2)} · {selectedListing.city}</p>
                </div>
                <button onClick={() => { setSelectedListing(null); setListingSearch(''); }} className="p-1 rounded text-zinc-400 hover:text-zinc-600" data-testid="clear-listing-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  value={listingSearch}
                  onChange={e => setListingSearch(e.target.value)}
                  placeholder="Search listings by title, ID, or seller..."
                  className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                  data-testid="listing-search-input"
                />
                {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 animate-spin" />}
                {listingResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto" data-testid="listing-search-results">
                    {listingResults.map(l => (
                      <button
                        key={l.listing_id}
                        onClick={() => { setSelectedListing(l); setListingResults([]); setListingSearch(''); }}
                        className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 transition-colors"
                        data-testid={`listing-option-${l.listing_id}`}
                      >
                        <div className="w-8 h-8 rounded bg-zinc-100 overflow-hidden flex-shrink-0">
                          {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-900 truncate">{l.title}</p>
                          <p className="text-[10px] text-zinc-500">${l.price?.toFixed(2)} · {l.city} · {l.seller_name || ''}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tier */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Promotion Tier *</label>
            <select
              value={selectedTier}
              onChange={e => setSelectedTier(e.target.value)}
              className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
              data-testid="tier-select"
            >
              <option value="">Select a tier...</option>
              {tiers.map(t => (
                <option key={t.id} value={t.id}>{t.name} — ${t.price_usd} / {t.duration_days}d</option>
              ))}
            </select>
          </div>

          {/* Date + Duration row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Duration Override (days)</label>
              <input
                type="number"
                min="1"
                value={durationOverride}
                onChange={e => setDurationOverride(e.target.value)}
                placeholder={selectedTierObj ? `${selectedTierObj.duration_days} (default)` : 'From tier'}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                data-testid="duration-override-input"
              />
            </div>
          </div>

          {/* Amount + Payment method row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Amount Paid ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amountPaid}
                onChange={e => setAmountPaid(e.target.value)}
                placeholder="0.00"
                className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                data-testid="amount-paid-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                data-testid="payment-method-select"
              >
                {PAYMENT_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Internal Note */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">
              <FileText className="w-3 h-3 inline mr-1" />Internal Note
            </label>
            <textarea
              value={internalNote}
              onChange={e => setInternalNote(e.target.value)}
              placeholder="Optional admin note (not visible to seller)..."
              rows={2}
              className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none resize-none"
              data-testid="internal-note-input"
            />
          </div>

          {/* Summary */}
          {selectedListing && selectedTierObj && (
            <div className="p-3 bg-[#002FA7]/5 rounded-lg border border-[#002FA7]/10 text-sm" data-testid="promotion-summary">
              <p className="font-semibold text-zinc-900 mb-1">Summary</p>
              <p className="text-zinc-600">
                <strong>{selectedListing.title}</strong> → {selectedTierObj.name} for{' '}
                {durationOverride && parseInt(durationOverride) > 0 ? durationOverride : selectedTierObj.duration_days} days
                {paymentMethod === 'comp' ? ' (complimentary)' : ` @ $${parseFloat(amountPaid || 0).toFixed(2)}`}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2.5 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50"
              data-testid="create-promo-cancel"
            >Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !selectedListing || !selectedTier}
              className="px-5 py-2.5 rounded-lg bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002585] disabled:opacity-50 flex items-center gap-2"
              data-testid="create-promo-submit"
            >
              {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
              <Zap className="w-3.5 h-3.5" /> Create Promotion
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expireTarget, setExpireTarget] = useState(null);
  const [extendTarget, setExtendTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter) params.status = filter;
      const { data } = await adminAPI.getPromotions(params);
      setPromotions(data.promotions || []);
      setTotalPages(data.pages || 1);
    } catch (err) {
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPromotions(); }, [page, filter]);

  const handleExpire = async () => {
    try {
      await adminAPI.expirePromotion(expireTarget.promotion_id);
      toast.success('Promotion expired');
      setExpireTarget(null);
      fetchPromotions();
    } catch (err) {
      toast.error('Failed to expire promotion');
    }
  };

  const handleExtend = async (days) => {
    try {
      await adminAPI.extendPromotion(extendTarget.promotion_id, { days });
      toast.success(`Promotion extended by ${days} days`);
      setExtendTarget(null);
      fetchPromotions();
    } catch (err) {
      toast.error('Failed to extend promotion');
    }
  };

  return (
    <div data-testid="admin-promotions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Promotions</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage all listing promotions</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002585] transition-colors"
            data-testid="create-promotion-btn"
          >
            <Plus className="w-4 h-4" /> Create Promotion
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/promotions/export-csv`}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-zinc-200 text-sm font-bold text-zinc-700 hover:bg-zinc-50 transition-colors"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-6" data-testid="promotion-filters">
        {[
          { value: '', label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'expired', label: 'Expired' },
        ].map(f => (
          <button key={f.value} onClick={() => { setFilter(f.value); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-[#002FA7] text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            data-testid={`filter-${f.value || 'all'}`}
          >{f.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden" data-testid="promotions-table">
        {loading ? (
          <div className="p-10 text-center">
            <Loader2 className="w-6 h-6 text-[#002FA7] animate-spin mx-auto" />
          </div>
        ) : promotions.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-400">No promotions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/50">
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Listing</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Seller</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Tier</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Period</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Source</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map(p => {
                  const isActive = p.status === 'active' && new Date(p.end_date) > new Date();
                  const method = p.payment_method || (p.session_id ? 'stripe' : 'stripe');
                  return (
                    <tr key={p.promotion_id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors" data-testid={`promo-row-${p.promotion_id}`}>
                      <td className="px-6 py-4">
                        <Link to={p.listing ? `/listing/${p.listing.listing_id}` : '#'} className="font-semibold text-zinc-900 hover:text-[#002FA7] line-clamp-1 max-w-[200px] block">
                          {p.listing?.title || 'Removed'}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-zinc-600">{p.seller?.name || p.seller?.email || '-'}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 text-xs font-medium"><Zap className="w-3 h-3 text-[#002FA7]" /> {p.tier_name}</span>
                      </td>
                      <td className="px-6 py-4 text-xs text-zinc-500">
                        {formatDate(p.start_date)} — {formatDate(p.end_date)}
                      </td>
                      <td className="px-6 py-4 font-mono font-semibold text-zinc-900">${p.amount_paid?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <PaymentBadge method={method} />
                      </td>
                      <td className="px-6 py-4">
                        {isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                            <Clock className="w-3 h-3" /> {timeRemaining(p.end_date)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 text-zinc-500">Expired</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setExtendTarget(p)}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-[#002FA7] border border-[#002FA7]/20 hover:bg-[#002FA7]/5 transition-colors"
                            title="Extend" data-testid={`extend-promo-${p.promotion_id}`}
                          >
                            <Plus className="w-3 h-3 inline mr-1" />Extend
                          </button>
                          {isActive && (
                            <button onClick={() => setExpireTarget(p)}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
                              title="Expire" data-testid={`expire-promo-${p.promotion_id}`}
                            >
                              <XCircle className="w-3 h-3 inline mr-1" />Expire
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-600 disabled:opacity-30" data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-600 disabled:opacity-30" data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      {expireTarget && <ExpireDialog promo={expireTarget} onClose={() => setExpireTarget(null)} onConfirm={handleExpire} />}
      {extendTarget && <ExtendDialog promo={extendTarget} onClose={() => setExtendTarget(null)} onConfirm={handleExtend} />}
      <CreatePromotionModal open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchPromotions} />
    </div>
  );
}
